import type { KeyboardEventHandler } from 'react'
import React, { useState } from 'react'
import { useTableFilterContext } from './Context'
import { FilterContainerBodyLayout } from './FilterContainer'
import type { TableFilterProps } from './types'
import { Dropdown, DropdownButton, Stack } from 'react-bootstrap'
import map from 'lodash/map'
import { NumberInput } from '@ztechapps/webui.input'
import { ShowIf } from '@ztechapps/webui.util'
import toNumber from 'lodash/toNumber'
import type { ListField } from '@ztechapps/common.hook'
import isNil from 'lodash/isNil'
import type { FilterQuery } from '@ztechapps/common.contract'

enum OperatorNameEnum {
  GreaterThan = 'Greater than',
  GreaterThanOrEqual = 'Greater than or equal to',
  LessThan = 'Less than',
  LessThanOrEqual = 'Less than or equal to',
  IsEqual = 'Is equal to',
  IsNotEqual = 'Is not equal to',
  IsBetween = 'Is between',
  IsNotBetween = 'Is not between',
}
const operatorValue: {
  [OperatorNameEnum.LessThan]: string
  [OperatorNameEnum.GreaterThan]: string
  [OperatorNameEnum.IsEqual]: string
  [OperatorNameEnum.LessThanOrEqual]: string
  [OperatorNameEnum.IsNotEqual]: string
  [OperatorNameEnum.GreaterThanOrEqual]: string
} = {
  [OperatorNameEnum.GreaterThan]: '$gt',
  [OperatorNameEnum.GreaterThanOrEqual]: '$gte',
  [OperatorNameEnum.LessThan]: '$lt',
  [OperatorNameEnum.LessThanOrEqual]: '$lte',
  [OperatorNameEnum.IsEqual]: '$eq',
  [OperatorNameEnum.IsNotEqual]: '$ne',
}

function getInitialValues(storedValue: FilterQuery<any>, field: ListField) {
  let initialOperatorName = OperatorNameEnum.GreaterThan
  let initialStartValue = 0
  let initialEndValue = 0
  for (const key in operatorValue) {
    if (!isNil(storedValue?.[operatorValue[key]])) {
      initialOperatorName = key as OperatorNameEnum
      initialStartValue = toNumber(storedValue[operatorValue[key]])
      break
    }
  }
  //if it is IsBetween
  if (storedValue?.$gte && storedValue?.$lte) {
    initialOperatorName = OperatorNameEnum.IsBetween
    initialStartValue = toNumber(storedValue.$gte)
    initialEndValue = toNumber(storedValue.$lte)
  }
  //if it is IsNotBetween
  if (storedValue?.$or) {
    initialOperatorName = OperatorNameEnum.IsNotBetween
    initialStartValue = toNumber(storedValue.$or[0][field.name].$lt)
    initialEndValue = toNumber(storedValue.$or[1][field.name].$gt)
  }

  return {
    initialOperatorName,
    initialStartValue,
    initialEndValue,
  }
}

export const NumberTableFilter = ({ field }: TableFilterProps) => {
  const { onFilter, getStoredFilter, setShowFilterContainer } = useTableFilterContext()
  const storedValue = getStoredFilter()

  const { initialEndValue, initialOperatorName, initialStartValue } = getInitialValues(storedValue, field)

  const [operatorName, setOperatorName] = useState<OperatorNameEnum>(initialOperatorName)
  const [startValue, setStartValue] = useState(initialStartValue)
  const [endValue, setNumber2Value] = useState(initialEndValue)

  const filterNow = () => {
    let value = null
    if (operatorName === OperatorNameEnum.IsBetween) {
      value = {
        $gte: startValue,
        $lte: endValue,
      }
    } else if (operatorName === OperatorNameEnum.IsNotBetween) {
      value = {
        $or: [{ [field.name]: { $lt: startValue } }, { [field.name]: { $gt: endValue } }],
      }
    } else {
      //other operators
      value = {
        [operatorValue[operatorName]]: startValue,
      }
    }

    const extraFilter = { [field.name]: value }
    onFilter(extraFilter)
  }

  const onKeyDown: KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      filterNow()
      setShowFilterContainer(false)
    }
  }

  return (
    <FilterContainerBodyLayout onOk={() => filterNow()}>
      <div style={{ minHeight: '20vh' }}>
        <Stack gap={3}>
          <OperatorDropdown value={operatorName} setValue={setOperatorName} />
          <NumberInput onKeyDown={onKeyDown} value={startValue} onChange={(e) => setStartValue(toNumber(e?.target?.value))} />
          <ShowIf condition={operatorName === OperatorNameEnum.IsBetween || operatorName === OperatorNameEnum.IsNotBetween}>
            <Stack>
              <div className="h4 text-center">AND</div>
              <NumberInput onKeyDown={onKeyDown} value={endValue} onChange={(e) => setNumber2Value(toNumber(e?.target?.value))} />
            </Stack>
          </ShowIf>
        </Stack>
      </div>
    </FilterContainerBodyLayout>
  )
}

export const OperatorDropdown = ({ value, setValue }: { value: OperatorNameEnum; setValue: (value: OperatorNameEnum) => void }) => {
  return (
    <DropdownButton title={value} variant="primary" className="d-grid">
      {map(OperatorNameEnum, (m) => (
        <Dropdown.Item onClick={() => setValue(m)} key={m}>
          {m}
        </Dropdown.Item>
      ))}
    </DropdownButton>
  )
}
